// Particle effects: the client half of the original's particle system (docs/platform/RenderFidelity.md
// "Particles"). The sim keeps the logical half of `BaseParticleEffect`/`TransientParticleEffect`;
// every sim step's particle view ("WMF2", crates/sim/src/view.rs `particles`) carries the effects
// with the dirty requests their `GraphicUpdate` served and the transients that were played. This
// module runs, once per sim step and in step order, what the original's graphics side does with
// them:
// - `BaseParticleEffect::GraphicUpdate` 0xd4e430 (`SetupEmitters` 0xd4dbb0,
//   `StartParticles_Graphical` 0xd4df50, `StopParticlesGraphical` 0xd4d9d0, `DestroyEmitters`, the
//   emitter start delays, `UpdateEmitterPositions` 0xd4cc90, `UpdateTargetAlpha` 0xd4e270);
// - `TransientParticleEffect`'s graphics (initialise, position, start, the delays; gone once no
//   emitter is active);
// - `BackgroundMan::GraphicUpdate` 0xb926a0 / `SetParticleWind` 0xb922c0 on the theme's wind effect;
// - `ParticleService::Update` 0x11bd870 at `ParticleService::m_Time` (1/60 s per presented frame,
//   the sim tick): `XEmitterEntity::Update` 0x11b8250 → `UpdateBasicEmitter` 0x11b6920 /
//   `UpdateMissileTrailEmitter` 0x11b7330 with `InitEmitter`, `InitParticle` (0x11c4950,
//   0x11c4e20 and the shape functions), `UpdateParticlePositionVelocity` 0x11b8640, the size,
//   colour and fade stages, `RemoveDeadParticles` 0x11bedd0, `EmitNewParticles` 0x11c0a00 /
//   0x11c1480;
// and draws every particle set as `XomOglDrawPsParticleSet` 0x11b3fd0 builds it (CPU quads:
// position ± size along the rotated or velocity-locked axes, velocity stretch, the
// `OffsetPhyToRend` offset, the frame cell, colour × alpha) with `Trans_PFX` (texture × vertex
// colour) and the emitter's blend (`MatAdd`: SRC_ALPHA/ONE, else SRC_ALPHA/ONE_MINUS_SRC_ALPHA).
//
// Determinism: a step is applied only once the definitions of the effects it starts are loaded
// (later steps wait in order), and the randomness is glibc's `rand()` (TYPE_3, seed 1) in this
// module's update order, so every run of the same match draws the same particles. The original
// draws from libc `rand()` shared by two worker threads; its sequence is not reproducible.
// Not ported: secondary and death emitters (no emitter references a container), deferred lights
// (none in the data), snow/rain emitters (never updated by the original), the alt-rate cycle
// (false in every file), the particle-quality rate modifier (1.0 at normal quality).
// Locators (`UpdateEmitterPositions` 0xd4cc90, VehicleVisuals 2026-10-24): an effect attached with
// `AttachToThisLocator` follows a locator of the parent `BaseMesh` as the renderer last posed it
// (`Particles.locator`). Without the transform flag it is placed at the locator with the z
// rotation of `XMatrix3::GetRotation` (xmatrix3.ts); with it its emitters become children of the
// locator node. A parent mesh the client has not loaded, or a locator its glb lacks, stands in
// with the effect's logical position.
// Draw order inside a layer: `XGLBase::NXParticle_Draw` 0x1190620 qsorts a batch's particle sets
// (`NXParticle_Sorter` 0x118f620: texture × 101987 + blend × 15443 + program × 1553, glibc's
// stable sort) and the scene's cull-sort hands over sets of one material in creation order; every
// emitter uses Trans_PFX (one program) and the GL texture names follow the textures' order in
// ParticleShaders.xom (gdb, hb-A 3700 and building-walk-A 2850: name = 82 + that index, the first
// texture 69), so a layer draws by (texture index, additive) and then by creation.
import * as THREE from 'three';
import { assetJson, assetUrl } from '../assets.ts';
import type { EffectView, ParticleStep, TransientView } from '../sim/view.ts';
import { getRotation } from './xmatrix3.ts';

type Rgba = { r: number; g: number; b: number; a: number };
type V3 = { x: number; y: number; z: number };
/** An emitter of particles/emitters/*.json (AssetConv: every field of the class chain). */
type EmitterJson = {
  class: string;
  fields: {
    Active: boolean; MaxParticles: number; LifeSpan: number; EmitterStaysAliveWhenExpired: boolean; MatAdd: boolean; AllowGroundZero: boolean;
    EmitterUsesWorldSpace: boolean; MinSpreadAngle: number; MaxSpreadAngle: number; Gravity: number; EmitterRotation: number;
    Rate: number; RateDeviation: number; FadeMode: number; ColourChange: number; SizeChange: number; EmitterShape: number;
    MaxEmitterRadius: number; MinEmitterRadius: number; InitialSpeed: number; InitialSpeedDeviation: number; InitialEnergy: number;
    EnergyDeviation: number; InitialSize: number; InitialSizeDeviation: number; FinalSizeFactor: number; InitialAlpha: number;
    TargetAlpha: number; FinalAlpha: number; FadeInEnergy: number; FadeOutEnergy: number; LinearVelocity: V3;
    RecurringLinearVelocityUpdate: boolean; WindVector: V3; OffsetPhyToRend: V3; Edges: number;
    MinRotationRate: number; MaxRotationRate: number; RotationDirection: number; RotationLockMode: number; FinalSpeedFactor: number;
    NumFrames: number; PageFrameWidth: number; RandomStartFrame: boolean; AnimationFrameRate: number; TargetSizeFactor: number;
    FirstSizeChangeStart: number; FirstSizeChangeEnd: number; SecondSizeChangeStart: number; SecondSizeChangeEnd: number;
    InitialColour: Rgba; TargetColour: Rgba; FinalColour: Rgba; FirstColourChangeStart: number; FirstColourChangeEnd: number;
    SecondColourChangeStart: number; SecondColourChangeEnd: number; VelocityStretchEnabled: boolean; VelocityStretchMultiplier: number;
    CylinderEmitterMinHeight: number; CylinderEmitterMaxHeight: number; MaxParticleGap?: number; RandomParticleOffset?: number;
  };
  texture?: { shape_name?: string };
};
type EffectJson = { effect: string; emitters: { emitter: string; delay_s: number; slot: number }[] };
type ParticleIndex = { effects: Record<string, string>; shapes: { name: string; material: { Textures?: { TextureMap?: { Texture?: { png?: string } } }[] } }[] };
type EffectDef = { emitters: EmitterJson[]; delays: number[]; textures: (THREE.Texture | null)[]; ranks: number[] };
/** `adopt`'s draw order inside a layer: the texture's index (× 2, + 1 additive) in steps of
 * RANK_STEP, then the creation index in steps of ADOPT_STEP (below 1 with up to 511 textures). */
const RANK_STEP = 1 / 1024, ADOPT_STEP = 2e-9;

// The enums of the emitter classes (re/out/enums.jsonl).
const SHAPE_DISC = 1, SHAPE_SPHERE = 2, SHAPE_CYLINDER = 3, SHAPE_XLINE = 4, SHAPE_XYRECT = 5, SHAPE_XYRECT_EDGES = 6;
const FADE_IN = 1, FADE_OUT = 2, FADE_IN_OUT = 3;
const COLOUR_NONE = 0, COLOUR_SINGLE = 1, COLOUR_TWO = 2;
const SIZE_SINGLE = 1, SIZE_TWO = 2;
const ROT_NONE = 0, ROT_CLOCKWISE = 1, ROT_COUNTER = 2, ROT_STATIC = 4;
const RLM_VELOCITY = 2;
/** `BaseParticleEffect::enumBaseParticleFlagLogicalToGraphical`. */
const DIRTY_STOP = 1, DIRTY_START = 2, DIRTY_INIT = 4, DIRTY_DESTROY = 8, DIRTY_DETACH_AFTER = 16, DIRTY_SET_ALLOCATOR = 64,
  DIRTY_SET_ALLOCATOR_TRANSFORM = 128, DIRTY_DETACH = 256, DIRTY_KEEP_ALIVE = 512;
/** `BaseParticleEffect::m_Ld.m_Flags`: started, `UpdateTargetAlpha` on, its `LogicUpdate` ran. */
const LD_STARTED = 4, LD_TARGET_ALPHA = 0x100, LD_UPDATED = 0x200;
/** `m_Ld.m_Flags`: attached with the locator's transform (`AttachToThisLocator`'s
 * `bUseTransform`, byte 0 bit 5), fixed rotation (`bFixedRotation`, byte 1 bit 2). */
const LD_TRANSFORM = 0x20, LD_FIXED_ROTATION = 0x400;
/** The view's parent object without a parent mesh. */
const NO_PARENT = 0xffffffff;
/** π/2 (0x3fc90fdb at 0x15aa45c): `UpdateEmitterPositions` adds it to a locator's z rotation. */
const HALF_PI = Math.fround(Math.PI / 2);
/** `BaseParticleEffect::m_Gd.m_Flags`. */
const GD_ATTACHED = 1, GD_ATTACHED_TRANSFORM = 2, GD_DETACH_PENDING = 4, GD_TARGET_ALPHA = 8, GD_DETACHED = 0x10, GD_TIME = 0x20;
/** `TransientParticleEffect::m_DirtyFlags`. */
const T_INIT = 1, T_START = 2, T_POSITION = 4, T_STOP = 8;

/** The particle layers of the scene script, by `SceneIdEnum` through `g_pLayerNames`. */
export type ParticleLayer = 'wind' | 'gameBG' | 'gameMIDBehind' | 'gameMID' | 'gameFG' | 'background' | 'back' | 'front' | 'waterFront' | 'additive';
const LAYERS: ParticleLayer[] = ['wind', 'gameBG', 'gameMIDBehind', 'gameMID', 'gameFG', 'background', 'back', 'front', 'waterFront', 'additive'];
function layerOf(scene: number): ParticleLayer {
  switch (scene) {
    case 0: return 'wind'; // FWDP_Transparent:WS_RenderGeometry_Wind
    case 1: case 5: return 'gameBG'; // WS_RenderGeometry_GameBG, WS_LandscapeBG
    case 2: return 'gameMIDBehind';
    case 3: return 'gameMID';
    case 4: case 7: return 'gameFG'; // WS_RenderGeometry_GameFG, WS_LandscapeFG
    case 6: case 15: return 'back'; // WS_LandscapeMID, ParticlesBack
    case 8: case 11: case 12: case 14: return 'background'; // the DRP_TransBG layers
    case 9: case 10: case 17: return 'waterFront';
    case 18: return 'additive';
    default: return 'front'; // ParticlesFront
  }
}

/** glibc `rand()` (`random_r` TYPE_3: r[i] = r[i-31] + r[i-3], output r >> 1, the first 310
 * values discarded) after `srand(1)`. */
class GlibcRand {
  private ring = new Int32Array(34);
  private at = 0;
  constructor(seed: number) {
    const r: number[] = [seed | 0];
    for (let k = 1; k < 31; k++) {
      const w = 16807 * (r[k - 1] % 127773) - 2836 * Math.trunc(r[k - 1] / 127773);
      r.push(w < 0 ? w + 2147483647 : w);
    }
    for (let k = 31; k < 344; k++) r.push(k < 34 ? r[k - 31] : (r[k - 31] + r[k - 3]) | 0);
    this.ring.set(r.slice(310, 344));
  }
  next(): number {
    const r = this.ring, i = this.at;
    const v = (r[(i + 3) % 34] + r[(i + 31) % 34]) | 0;
    r[i] = v;
    this.at = (i + 1) % 34;
    return v >>> 1;
  }
}
const rng = new GlibcRand(1);
/** `((rand() + 0x309) & 0x7fffffff) * 2^-30 - 1`: U[-1, 1). */
const U = (): number => ((rng.next() + 0x309) & 0x7fffffff) * 9.313225746154785e-10 - 1;
/** `((rand() + 0x309) & 0x7fffffff) * 2^-31`: U[0, 1). */
const U01 = (): number => ((rng.next() + 0x309) & 0x7fffffff) * 4.656612873077393e-10;
const DEG = 0.01745329238474369;
/** The stage windows' clamp (`t > 1` and NaN give 1). */
const clamp01 = (t: number): number => (t <= 1 ? (t >= 0 ? t : 0) : 1);

/** The draw's rotation: the angle quantised to 1024 steps (`((int)((rot + 3.142) * 163) &
 * 0x3ff) * 0.00614 - 3.142`) and the polynomial sine and cosine of `XomOglDrawPsParticleSet`. */
function fastSinCos(rot: number): [number, number] {
  const x = (Math.trunc((rot + 3.142) * 163.0) & 0x3ff) * 0.006140000186860561 - 3.1419999599456787;
  const x2 = x * x;
  return [x - 0.16660000383853912 * x * x2 + x * x2 * x2 * 0.006500000134110451,
    1 - 0.5 * x2 + 0.041999999433755875 * x2 * x2 - x2 * x2 * x2 * 0.0012000000569969416];
}

/** `ParticleMan`'s effect name hash (djb2-xor of the name). */
function nameHash(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = (Math.imul(h, 33) ^ s.charCodeAt(i)) >>> 0;
  return h;
}

const VERT = /* glsl */ `
in vec3 position;
in vec2 uv;
in vec4 colour;
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
out vec2 slTexCoord1;
out vec4 slColour;
void main() {
  slTexCoord1 = uv;
  slColour = colour;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;
/** `Root.SecondaryShaders.Trans_PFX`: texture × vertex colour. */
const FRAG = /* glsl */ `
precision highp float;
uniform sampler2D Diffuse1;
in vec2 slTexCoord1;
in vec4 slColour;
out vec4 OUTColor;
void main() { OUTColor = texture(Diffuse1, slTexCoord1) * slColour; }`;

/** `XEmitterEntity` with its emitter container (a copy of the file's fields: the runtime writes
 * some of them) and its `BasicEmitterState`/`MissileTrailEmitterState`. */
class Emitter {
  readonly f: EmitterJson['fields'];
  readonly trail: boolean;
  readonly layer: ParticleLayer;
  /** The texture's index in ParticleShaders.xom (the GL texture name's order). */
  readonly texRank: number;
  readonly mesh: THREE.Mesh;
  /** `m_bEmitterDieFlag`: out of the service, no longer drawn. */
  dead = false;
  /** `m_EmitterXForm`: translation and the z rotation's cosine and sine (world-space emitters). */
  private xf = { x: 0, y: 0, z: 0, c: 1, s: 0 };
  /** The x scale of the emitter instance: each transform attach of a reversed effect negates it
   * (`UpdateEmitterPositions`: `SetScale(-x, y, z)`). */
  sx = 1;
  private initialised = false;
  private lastUpdate = 0;
  private dt = 0;
  private expiry = 0;
  private spawnPot = 0;
  private rateAdjust = 0;
  private num = 0;
  private lastActive = -1;
  private lastFreed = -1;
  peakReached = false;
  private dieFlag = false;
  private groundZero = 0;
  private enableGroundZero = false;
  private maxWindSet = false;
  private maxWindSpeed = 0;
  private velocityCap = 0;
  private velocityCapSq = 0;
  private enableOffsets = false;
  private speedMul = 1;
  private spreadMin = 0;
  private spreadMax = 0;
  /** `MissileTrailEmitterState::vEmitterPrevPosition`. */
  private prev = { x: 0, y: 0, z: 0 };
  // The particle slots (MaxParticles; a slot is free while its size is 0).
  private pos: Float32Array; // x, y, z, size
  private vel: Float32Array; // x, y, z, w
  private oldVel: Float32Array;
  private energy: Float32Array;
  private alpha: Float32Array;
  private colour: Float32Array | null;
  private rotRate: Float32Array | null;
  private rot: Float32Array | null;
  private frame: Float32Array | null;
  private size0: Float32Array | null;
  private offsets: Float32Array; // current, target, frames left
  private frameP = 1;
  private frameW = 1;
  private geo: THREE.BufferGeometry;
  private aPos: THREE.BufferAttribute;
  private aUv: THREE.BufferAttribute;
  private aCol: THREE.BufferAttribute;

  constructor(def: EmitterJson, tex: THREE.Texture | null, layer: ParticleLayer, texRank: number) {
    this.f = { ...def.fields, WindVector: { ...def.fields.WindVector } };
    this.trail = def.class === 'XMissileTrailEmitter';
    this.layer = layer;
    this.texRank = texRank;
    const f = this.f;
    // `XEmitterEntity::Initialize` 0x11b5b20: the size deviation is clamped to InitialSize − 0.01
    // when it would reach below 0.01 (two-stage size change, or InitialSize 0.01).
    if (f.InitialSize - f.InitialSizeDeviation < 0.01 && (f.SizeChange === SIZE_TWO || f.InitialSize === 0.01)) {
      f.InitialSizeDeviation = f.InitialSize - 0.01;
    }
    const n = Math.max(0, f.MaxParticles | 0);
    this.pos = new Float32Array(n * 4);
    this.vel = new Float32Array(n * 4);
    this.oldVel = new Float32Array(n * 4);
    this.energy = new Float32Array(n);
    this.alpha = new Float32Array(n);
    this.colour = f.ColourChange !== COLOUR_NONE ? new Float32Array(n * 4) : null;
    this.rotRate = f.RotationDirection !== ROT_NONE ? new Float32Array(n) : null;
    this.rot = f.RotationDirection !== ROT_NONE ? new Float32Array(n) : null;
    this.frame = f.NumFrames >= 2 ? new Float32Array(n) : null;
    this.size0 = f.InitialSizeDeviation !== 0 ? new Float32Array(n) : null;
    this.offsets = new Float32Array(n * 3);
    if (f.NumFrames >= 2 && f.PageFrameWidth) {
      this.frameP = f.PageFrameWidth;
      this.frameW = 1 / f.PageFrameWidth;
    }
    // `InitEmitter(XBasicEmitter)`: the size factors are raised to 1e-9.
    if (f.SizeChange === SIZE_TWO && f.TargetSizeFactor < 1e-9) f.TargetSizeFactor = 1e-9;
    if (f.SizeChange !== 0 && f.FinalSizeFactor < 1e-9) f.FinalSizeFactor = 1e-9;
    this.geo = new THREE.BufferGeometry();
    this.aPos = new THREE.BufferAttribute(new Float32Array(n * 12), 3).setUsage(THREE.DynamicDrawUsage);
    this.aUv = new THREE.BufferAttribute(new Float32Array(n * 8), 2).setUsage(THREE.DynamicDrawUsage);
    this.aCol = new THREE.BufferAttribute(new Float32Array(n * 16), 4).setUsage(THREE.DynamicDrawUsage);
    this.geo.setAttribute('position', this.aPos);
    this.geo.setAttribute('uv', this.aUv);
    this.geo.setAttribute('colour', this.aCol);
    const idx = new (n * 4 > 65535 ? Uint32Array : Uint16Array)(n * 6);
    for (let i = 0; i < n; i++) idx.set([4 * i, 4 * i + 1, 4 * i + 2, 4 * i, 4 * i + 2, 4 * i + 3], 6 * i);
    this.geo.setIndex(new THREE.BufferAttribute(idx, 1));
    this.geo.setDrawRange(0, 0);
    const mat = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3, vertexShader: VERT, fragmentShader: FRAG, uniforms: { Diffuse1: { value: tex } },
      transparent: true, depthTest: false, depthWrite: false, side: THREE.DoubleSide,
      blending: THREE.CustomBlending, blendSrc: THREE.SrcAlphaFactor, blendDst: f.MatAdd ? THREE.OneFactor : THREE.OneMinusSrcAlphaFactor,
    });
    this.mesh = new THREE.Mesh(this.geo, mat);
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
  }

  /** `XEmitterEntity::SetActive(bActive, bStopAlive)` 0x11b6740. */
  setActive(active: boolean, stopAlive: boolean): void {
    if (this.dead) return;
    this.f.Active = active;
    if (!this.initialised) return;
    if (active && this.f.LifeSpan > 0 && this.expiry < this.lastUpdate) this.expiry = this.lastUpdate + this.f.LifeSpan;
    if (this.trail) this.prev = { x: this.xf.x, y: this.xf.y, z: this.xf.z };
    if (stopAlive) this.energy.fill(0, 0, this.lastActive + 1);
  }

  /** `XEmitterEntity::SetPosition` 0x11b6050 / `SetOrientation` 0x11b61d0: world-space emitters
   * keep them in `m_EmitterXForm`, the others move their instance. */
  place(x: number, y: number, z: number, rz: number): void {
    if (this.dead) return;
    if (this.f.EmitterUsesWorldSpace) {
      Object.assign(this.xf, { x, y, z, c: Math.cos(rz), s: Math.sin(rz) });
    } else {
      this.mesh.matrixAutoUpdate = true;
      this.mesh.position.set(x, y, z);
      this.mesh.rotation.set(0, 0, rz);
      this.mesh.scale.set(this.sx, 1, 1);
    }
  }

  /** The instance as a child of a locator node (`UpdateEmitterPositions`' transform mode): its
   * world transform is the locator's world matrix `m` times its own x scale. World-space emitters
   * take nothing: the original sets no `m_EmitterXForm` in that mode, and no shipped effect
   * attached with the transform has a world-space emitter. */
  follow(m: THREE.Matrix4): void {
    if (this.dead || this.f.EmitterUsesWorldSpace) return;
    const w = this.mesh.matrix.copy(m).elements;
    for (let k = 0; k < 3; k++) w[k] *= this.sx;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.matrixWorldNeedsUpdate = true;
  }

  /** `XEmitterEntity::SetMaxWindSpeed` 0x11b66e0. */
  setMaxWind(speed: number, cap: number): void {
    this.maxWindSet = true;
    this.maxWindSpeed = speed;
    this.velocityCap = cap;
    this.velocityCapSq = cap * cap;
  }

  /** `XEmitterEntity::SetWindVector` / `SetEdges`. */
  setWind(x: number, y: number, edges: number): void {
    if (this.dead) return;
    Object.assign(this.f.WindVector, { x, y, z: 0 });
    this.f.Edges = edges;
  }

  /** `ParticleService::SetGroundZero` (the water level; only for `AllowGroundZero`). */
  setGroundZero(level: number): void {
    this.groundZero = level;
    this.enableGroundZero = this.f.AllowGroundZero;
  }

  /** `XEmitterEntity::ForceWindDirection` 0x11b6440: the velocities restart from min(|vx|,
   * 10 wx) (10 wx for wind ≤ 0) and blend from the old ones over 60 frames; the render offsets
   * move to their share of the wind. */
  forceWind(): void {
    if (this.dead || !this.initialised) return;
    const w = this.f.WindVector;
    const share = this.maxWindSet ? Math.hypot(w.x, w.y, w.z) / this.maxWindSpeed : 0;
    const wx = w.x * 10;
    for (let i = 0; i <= this.lastActive; i++) {
      const o = 4 * i;
      this.oldVel.set(this.vel.subarray(o, o + 4), o);
      const a = Math.abs(this.vel[o]);
      this.vel[o] = a < wx ? a : wx;
      if (this.maxWindSet) {
        this.offsets[3 * i + 2] = 60;
        this.offsets[3 * i + 1] = this.offsets[3 * i] * share;
      }
    }
  }

  /** Emits or has live particles (`BaseParticleEffect::AnyEmittersActive_Graphical`). */
  busy(): boolean {
    return !this.dead && (this.f.Active || this.num > 0);
  }

  /** `XEmitterEntity::Update(time)`: `InitEmitter` on the first call, then the emitter's update. */
  update(time: number): void {
    if (this.dead) return;
    if (!this.initialised) {
      this.initEmitter(time);
      return;
    }
    this.dt = time - this.lastUpdate;
    this.lastUpdate = time;
    const f = this.f;
    for (let i = 0; i <= this.lastActive; i++) this.energy[i] -= this.dt;
    if (this.trail) {
      // `UpdateMissileTrailEmitter`: trail particles do not move.
      if (!f.Active) this.prev = { x: this.xf.x, y: this.xf.y, z: this.xf.z };
    } else this.move();
    this.stages();
    if (this.expire(time)) {
      this.dead = true;
      return;
    }
    this.removeDead(0.01);
    // The rate: (Rate × RateModifier + the last deviation) × dt + the carried fraction.
    const pot = (f.Rate * 1.0 + this.rateAdjust) * this.dt + this.spawnPot;
    this.rateAdjust = U() * f.RateDeviation;
    const n = Math.floor(pot);
    this.spawnPot = pot - n;
    if (n <= 0 || this.num >= f.MaxParticles || !f.Active) return;
    if (this.trail) this.emitTrail();
    else this.emit(Math.min(n, f.MaxParticles - this.num));
  }

  /** `InitEmitter(XParticleEmitter)` 0x11bf510 + `InitEmitter(XBasicEmitter)` 0x11c01e0; a
   * missile trail's `InitEmitter(XMissileTrailEmitter)` 0x11c1080 then sets `m_Rate` to 10000
   * (the file's rate is unused: the trail emits every update, gated by the distance; gdb, hb-A
   * 3531-3645: rate 10000 for BazookaTrail_Rings_001 and _Trail_001). */
  private initEmitter(time: number): void {
    const f = this.f;
    this.initialised = true;
    this.enableOffsets = f.OffsetPhyToRend.x !== 0 || f.OffsetPhyToRend.y !== 0 || f.OffsetPhyToRend.z !== 0;
    this.expiry = f.LifeSpan === -1 ? 0 : f.LifeSpan + time;
    this.lastUpdate = time;
    this.spreadMin = (f.MinSpreadAngle * 0.5) / 90;
    this.spreadMax = (0.5 * f.MaxSpreadAngle) / 90;
    this.speedMul = Math.pow(f.FinalSpeedFactor, 1 / f.InitialEnergy);
    for (let o = 0; o < this.oldVel.length; o += 4) this.oldVel[o + 3] = -1;
    this.prev = { x: this.xf.x, y: this.xf.y, z: this.xf.z };
    if (this.trail) f.Rate = 10000;
  }

  /** The size, fade, colour, frame and rotation stages (`UpdateParticleSize*`,
   * `UpdateParticleAlphaFade*`, the colour stages, the frame and rotation loops). */
  private stages(): void {
    const f = this.f, n = this.lastActive, e = this.energy, p = this.pos, E0 = f.InitialEnergy;
    if (f.SizeChange === SIZE_SINGLE) {
      const k = (f.FinalSizeFactor - 1) * f.InitialSize / E0;
      for (let i = 0; i <= n; i++) if (p[4 * i + 3] > 0) p[4 * i + 3] = Math.max(1e-4, (E0 - e[i]) * k + f.InitialSize);
    } else if (f.SizeChange === SIZE_TWO) {
      const k1 = 1 / (f.FirstSizeChangeStart - f.FirstSizeChangeEnd), k2 = 1 / (f.SecondSizeChangeStart - f.SecondSizeChangeEnd);
      const T = f.TargetSizeFactor, F = f.FinalSizeFactor;
      for (let i = 0; i <= n; i++) {
        if (!(p[4 * i + 3] > 1e-9)) continue;
        const s0 = this.size0 ? this.size0[i] : f.InitialSize;
        const t1 = clamp01((f.FirstSizeChangeStart - e[i]) * k1), t2 = clamp01((f.SecondSizeChangeStart - e[i]) * k2);
        p[4 * i + 3] = Math.max(1e-4, t2 * (F * s0 - T * s0) + t1 * (T - 1) * s0 + s0);
      }
    }
    const a = this.alpha;
    if (f.FadeMode === FADE_IN) {
      const k = 1 / (E0 - f.FadeInEnergy);
      for (let i = 0; i <= n; i++) a[i] = Math.min((E0 - e[i]) * k, 1) * (f.FinalAlpha - f.InitialAlpha) + f.InitialAlpha;
    } else if (f.FadeMode === FADE_OUT) {
      const k = 1 / f.FadeOutEnergy;
      for (let i = 0; i <= n; i++) a[i] = Math.max(1 - e[i] * k, 0) * (f.FinalAlpha - f.InitialAlpha) + f.InitialAlpha;
    } else if (f.FadeMode === FADE_IN_OUT) {
      const kin = E0 - f.FadeInEnergy >= 1e-9 ? 1 / (E0 - f.FadeInEnergy) : 1e9;
      const kout = f.FadeOutEnergy >= 1e-9 ? 1 / f.FadeOutEnergy : 1e9;
      for (let i = 0; i <= n; i++) {
        a[i] = Math.min((E0 - e[i]) * kin, 1) * (f.TargetAlpha - f.InitialAlpha) + f.InitialAlpha + Math.max(1 - e[i] * kout, 0) * (f.FinalAlpha - f.TargetAlpha);
      }
    }
    const c = this.colour;
    if (c && f.ColourChange === COLOUR_SINGLE) {
      const I = f.InitialColour, Fc = f.FinalColour;
      for (let i = 0; i <= n; i++) {
        const t = 1 - e[i] / E0;
        c.set([(Fc.r - I.r) * t + I.r, (Fc.g - I.g) * t + I.g, t * (Fc.b - I.b) + I.b, f.InitialAlpha], 4 * i);
      }
    } else if (c && f.ColourChange === COLOUR_TWO) {
      const I = f.InitialColour, T = f.TargetColour, Fc = f.FinalColour;
      const k1 = 1 / (f.FirstColourChangeStart - f.FirstColourChangeEnd), k2 = 1 / (f.SecondColourChangeStart - f.SecondColourChangeEnd);
      for (let i = 0; i <= n; i++) {
        const t1 = clamp01((f.FirstColourChangeStart - e[i]) * k1), t2 = clamp01((f.SecondColourChangeStart - e[i]) * k2);
        c[4 * i] = (Fc.r - T.r) * t2 + (T.r - I.r) * t1 + I.r;
        c[4 * i + 1] = (Fc.g - T.g) * t2 + (T.g - I.g) * t1 + I.g;
        c[4 * i + 2] = t2 * (Fc.b - T.b) + t1 * (T.b - I.b) + I.b;
      }
    }
    if (this.frame && f.AnimationFrameRate >= 1) {
      for (let i = 0; i <= n; i++) this.frame[i] = Math.trunc((E0 - e[i]) * f.AnimationFrameRate) & (f.NumFrames - 1);
    }
    if (this.rot && this.rotRate && (f.RotationDirection & ~ROT_STATIC) !== ROT_NONE) {
      for (let i = 0; i <= n; i++) this.rot[i] += this.rotRate[i] * this.dt;
    }
  }

  /** `UpdateParticlePositionVelocity<XBasicEmitter>` 0x11b8640 (no secondary emitters; the
   * gravity matrix is the identity in every file). */
  private move(): void {
    const f = this.f, dt = this.dt, n = this.lastActive, v = this.vel, p = this.pos;
    if (n < 0) return;
    const decaying = Math.abs(this.speedMul - 1) > 1e-9;
    const mul = decaying ? Math.pow(this.speedMul, dt) : 1;
    const gy = -(f.Gravity * dt);
    const lin = f.RecurringLinearVelocityUpdate ? f.LinearVelocity : { x: 0, y: 0, z: 0 };
    if (decaying || f.RecurringLinearVelocityUpdate) {
      for (let o = 0; o <= 4 * n; o += 4) {
        v[o] *= mul; v[o + 1] *= mul; v[o + 2] *= mul;
        p[o] += (v[o] + lin.x) * dt; p[o + 1] += (v[o + 1] + lin.y) * dt; p[o + 2] += (v[o + 2] + lin.z) * dt;
      }
    } else {
      // Without decay the velocity blends from the one before `ForceWindDirection` over the
      // render offsets' frame counter (60 → 0).
      const ov = this.oldVel;
      for (let i = 0; i <= n; i++) {
        const o = 4 * i;
        let vx = v[o], vy = v[o + 1], vz = v[o + 2];
        const left = this.enableOffsets ? this.offsets[3 * i + 2] : 0;
        if (left <= 0 || (ov[o] === 0 && ov[o + 1] === 0 && ov[o + 2] === 0 && ov[o + 3] === -1)) {
          ov.set([0, 0, 0, -1], o);
        } else {
          const b = 1 - left / 60, ob = 1 - b;
          vx = vx * b + ov[o] * ob; vy = ov[o + 1] * ob + vy * b; vz = vz * b + ov[o + 2] * ob;
        }
        p[o] += vx * dt; p[o + 1] += vy * dt; p[o + 2] += vz * dt;
      }
    }
    // Wind and gravity; only gravity while the velocity exceeds the wind's cap.
    const ay = f.WindVector.y * dt + gy, ax = f.WindVector.x * dt;
    for (let o = 0; o <= 4 * n; o += 4) {
      const capped = this.maxWindSet && dt > 0 && v[o + 1] * v[o + 1] + v[o] * v[o] > this.velocityCapSq;
      v[o] += capped ? 0 : ax;
      v[o + 1] += capped ? gy : ay;
    }
    if (this.enableOffsets) {
      const of = this.offsets;
      for (let k = 0; k <= 3 * n; k += 3) {
        const target = of[k + 1];
        if (target === 0) continue;
        const left = of[k + 2];
        of[k + 2] = left - 1;
        of[k] += (target - of[k]) / left;
        if (left - 1 <= 0) { of[k] = target; of[k + 1] = 0; }
      }
    }
  }

  /** The emitter's lifetime (`UpdateBasicEmitter`/`UpdateMissileTrailEmitter`): an expired one
   * stops emitting and dies once its particles are gone; an inactive one does not age. */
  private expire(now: number): boolean {
    const f = this.f;
    if (f.LifeSpan === -1) return false;
    let gone = false;
    if (!f.Active) {
      if (this.expiry < now) gone = true;
      else this.expiry += this.dt;
    } else if (this.expiry < now) {
      f.Active = false;
      gone = true;
    }
    if (gone && this.num === 0 && !f.EmitterStaysAliveWhenExpired && !this.dieFlag) {
      this.dieFlag = true;
      return true;
    }
    return false;
  }

  /** `RemoveDeadParticles(XParticleEmitter*, BaseEmitterState*, float)` 0x11bedd0: rectangle
   * shapes keep (resurrect) particles inside the rectangle and kill those outside; particles
   * below ground zero and spent ones die. */
  private removeDead(resurrect: number): void {
    const f = this.f, p = this.pos, e = this.energy;
    const rect = f.EmitterShape === SHAPE_XYRECT || f.EmitterShape === SHAPE_XYRECT_EDGES;
    for (let i = 0; i <= this.lastActive; i++) {
      let kill = false;
      if (rect) {
        if (Math.abs(p[4 * i]) <= f.MinEmitterRadius && Math.abs(p[4 * i + 1]) <= f.MaxEmitterRadius) {
          if (e[i] <= 0) e[i] = resurrect;
        } else if (e[i] > 0) {
          e[i] = 0;
          kill = true;
        }
      }
      if (!kill && this.enableGroundZero && p[4 * i + 1] < this.groundZero) { e[i] = 0; kill = true; }
      if ((kill || e[i] <= 0) && p[4 * i + 3] > 0) this.free(i);
    }
  }

  /** A slot freed with `RemoveDeadParticles`' bookkeeping of `nLastActive` and `nLastFreed`. */
  private free(i: number): void {
    const p = this.pos;
    p[4 * i + 3] = 0;
    this.num--;
    if (i !== this.lastActive) {
      this.lastFreed = i;
      return;
    }
    let k = this.lastActive;
    while (k >= 0 && p[4 * k + 3] === 0) k--;
    this.lastActive = k;
    if (this.lastFreed > k) {
      let j = k;
      while (j >= 0 && p[4 * j + 3] !== 0) j--;
      this.lastFreed = j;
    }
  }

  /** `EmitNewParticles`' slot: the last freed one (the next free one below it becomes the
   * hint), else a new one at the end. */
  private alloc(): number {
    if (this.lastFreed === -1) return ++this.lastActive;
    const slot = this.lastFreed;
    let j = slot - 1;
    while (j >= 0 && this.pos[4 * j + 3] !== 0) j--;
    if (j < 0) {
      j = this.lastActive;
      while (j > slot && this.pos[4 * j + 3] !== 0) j--;
      if (j <= slot) j = -1;
    }
    this.lastFreed = j;
    return slot;
  }

  /** `EmitNewParticles(XBasicEmitter*, ...)` 0x11c0a00. */
  private emit(n: number): void {
    for (let k = 0; k < n && this.num < this.f.MaxParticles; k++) {
      this.initParticle(this.alloc());
      this.num++;
    }
  }

  /** `EmitNewParticles(XMissileTrailEmitter*, ...)` 0x11c1480: int(d / gap + 0.5) particles on
   * the segment from the previous emission point (d in [0.001, 1000], else the point moves to
   * the emitter), with one jitter; below one gap the point stays (hb-A 3543-3552: the previous
   * position holds while the rocket moves 20 units a frame towards the 192 gap). */
  private emitTrail(): void {
    const f = this.f, cur = { x: this.xf.x, y: this.xf.y, z: this.xf.z }, prev = this.prev;
    if (prev.x * prev.x + prev.y * prev.y + prev.z * prev.z <= 1e-18) { this.prev = cur; return; }
    const dx = cur.x - prev.x, dy = cur.y - prev.y, dz = cur.z - prev.z;
    const d = Math.hypot(dx, dy, dz);
    if (d < 0.001 || d > 1000) { this.prev = cur; return; }
    if (!f.MaxParticleGap) f.MaxParticleGap = 1;
    if (d / f.MaxParticleGap < 1) return;
    this.prev = cur;
    const n = Math.trunc(d / f.MaxParticleGap + 0.5), R = f.RandomParticleOffset ?? 0;
    const jx = U() * R, jy = U() * R, jz = U() * R;
    for (let k = 1; k <= n && this.num < f.MaxParticles; k++) {
      Object.assign(this.xf, { x: prev.x + dx * (k / n) + jx, y: prev.y + dy * (k / n) + jy, z: prev.z + dz * (k / n) + jz });
      this.initParticle(this.alloc());
      this.num++;
    }
    Object.assign(this.xf, cur);
  }

  /** `XParticleEmitter::InitParticle` 0x11c4950, then `XBasicEmitter::InitParticle` 0x11c4e20
   * (`XMissileTrailEmitter`'s is a jump to it). */
  private initParticle(i: number): void {
    const f = this.f, p = this.pos, v = this.vel, o = 4 * i;
    this.alpha[i] = f.InitialAlpha;
    this.energy[i] = U() * f.EnergyDeviation + f.InitialEnergy;
    // The direction: y in [1 - max, 1 - min) of the spread (degrees / 180), x within 1 - |y|.
    const y = U01() * (this.spreadMax - this.spreadMin) + (1 - this.spreadMax);
    const x = (1 - Math.abs(y)) * U();
    rng.next();
    const k = 1 / Math.sqrt(x * x + y * y);
    const speed = U() * f.InitialSpeedDeviation + f.InitialSpeed;
    let vx = x * k * speed, vy = y * k * speed;
    if (f.EmitterUsesWorldSpace) [vx, vy] = [this.xf.c * vx - this.xf.s * vy, this.xf.s * vx + this.xf.c * vy];
    if (f.EmitterRotation !== 0) {
      const c = Math.cos(f.EmitterRotation * DEG), s = Math.sin(f.EmitterRotation * DEG);
      [vx, vy] = [c * vx - s * vy, s * vx + c * vy];
    }
    if (!f.RecurringLinearVelocityUpdate) { vx += f.LinearVelocity.x; vy += f.LinearVelocity.y; }
    v.set([vx, vy, 0, 0], o);
    if (this.lastActive + 1 === f.MaxParticles - 1) this.peakReached = true;
    // The shape (jump table 0x1604328) places the particle and draws its size.
    let size: number;
    switch (f.EmitterShape) {
      case SHAPE_DISC: case SHAPE_CYLINDER: {
        // InitParticleCircularPosition 0x11c2770 (+ the height of InitParticleCylindricalPosition).
        const r = f.MinEmitterRadius <= 0 ? f.MaxEmitterRadius * U01() : U01() * (f.MaxEmitterRadius - f.MinEmitterRadius) + f.MinEmitterRadius;
        const a = U() * Math.PI;
        p.set([Math.cos(a) * r, 0, r * Math.sin(a)], o);
        if (f.EmitterShape === SHAPE_CYLINDER) {
          const lo = f.CylinderEmitterMinHeight, h = U() * (lo <= 0 ? f.CylinderEmitterMaxHeight : f.CylinderEmitterMaxHeight - lo);
          p[o + 1] = lo <= 0 ? h : h >= 0 ? h + lo : h - lo;
        }
        size = this.drawSize();
        break;
      }
      case SHAPE_SPHERE: {
        // InitParticleSphericalPosition 0x11c2980: along the velocity's direction (a random
        // one when it is zero, reversed for a negative speed).
        let dx = vx, dy = vy, dz = 0;
        if (dx * dx + dy * dy <= 1e-18) {
          dx = U(); dy = U(); dz = U();
          if (f.InitialSpeed < 0) { dx = -dx; dy = -dy; dz = -dz; }
        }
        const q = 1 / Math.sqrt(dx * dx + dy * dy + dz * dz);
        const r = f.MinEmitterRadius <= 0 ? U01() * f.MaxEmitterRadius : U01() * (f.MaxEmitterRadius - f.MinEmitterRadius) + f.MinEmitterRadius;
        p.set([dx * q * r, dy * q * r, r * dz * q], o);
        size = this.drawSize();
        break;
      }
      case SHAPE_XLINE:
        p.set([U() * f.MaxEmitterRadius, 0, 0], o);
        size = this.drawSize();
        break;
      case SHAPE_XYRECT:
        p.set([U() * f.MinEmitterRadius, U() * f.MaxEmitterRadius, 0], o);
        size = this.drawSize();
        break;
      case SHAPE_XYRECT_EDGES: {
        this.rectEdges(o);
        p[o + 2] = 0;
        size = this.drawSize();
        // Edge particles take the wind: vx = wx (1 + 10 |U|) within the component cap,
        // vy = wy (1 + 0.1 U).
        const w = f.WindVector;
        const ex = Math.abs(U()) * w.x * 10 + w.x;
        v[o] = Math.abs(ex) > this.velocityCap ? Math.sign(ex) * this.velocityCap : ex;
        v[o + 1] = (U() * 0.1 + 1) * w.y;
        break;
      }
      default: // Point
        p.set([0, 0, 0], o);
        size = U() * f.InitialSizeDeviation + f.InitialSize;
    }
    p[o + 3] = size <= 0 ? 0.001 : size;
    if (this.enableOffsets) {
      let off = U01() * f.OffsetPhyToRend.x;
      if (this.maxWindSet) off *= Math.hypot(f.WindVector.x, f.WindVector.y, f.WindVector.z) / this.maxWindSpeed;
      this.offsets.set([off, 0, 0], 3 * i);
    }
    if (f.EmitterUsesWorldSpace) { p[o] += this.xf.x; p[o + 1] += this.xf.y; p[o + 2] += this.xf.z; }
    if (this.size0) this.size0[i] = p[o + 3];
    if (this.rot && this.rotRate) {
      // InitParticleRotation 0x11c2e00: deg/s; the angle starts at the same value.
      const mn = f.MinRotationRate, mx = f.MinRotationRate === f.MaxRotationRate ? f.MaxRotationRate + 0.001 : f.MaxRotationRate;
      const r = f.RotationDirection === ROT_CLOCKWISE ? U01() * (mx - mn) : f.RotationDirection === ROT_COUNTER ? U01() * -(mx - mn) : U() * (mx - mn);
      this.rotRate[i] = this.rot[i] = (r < 0 ? r - mn : r + mn) * DEG;
    }
    if (this.colour) this.colour.set([f.InitialColour.r, f.InitialColour.g, f.InitialColour.b, f.InitialColour.a], o);
    if (this.frame) this.frame[i] = f.RandomStartFrame ? U01() * f.NumFrames : 0;
  }

  /** The shape functions' size: InitialSize ± deviation; if ≤ 0, InitialSize + U[0,1) ×
   * deviation / 2. */
  private drawSize(): number {
    const f = this.f;
    const s = U() * f.InitialSizeDeviation + f.InitialSize;
    return s > 0 ? s : U01() * (0.5 * f.InitialSizeDeviation) + f.InitialSize;
  }

  /** `InitParticleXYRectEdges` 0x11c3200: before the peak anywhere in the rectangle, then on the
   * first edge strip of `Edges` from a random start (2 right, 4 left, 8 top; 1 keeps the slot's
   * old position). */
  private rectEdges(o: number): void {
    const f = this.f, p = this.pos, w = f.MinEmitterRadius, h = f.MaxEmitterRadius;
    if (!this.peakReached) {
      p[o + 1] = U() * h;
      p[o] = U() * w;
      return;
    }
    const EDGES = [1, 2, 4, 8];
    for (let k = Math.trunc(Math.abs(U() * 4)), tries = 0; tries < 4; tries++, k = (k & 3) + 1) {
      const e = EDGES[k & 3];
      if (!(f.Edges & e)) continue;
      if (e === 4) { p[o] = 0.4 * w * Math.abs(U()) - w; p[o + 1] = U() * h; return; }
      if (e === 2) { p[o] = w - Math.abs(U()) * 0.4 * w; p[o + 1] = U() * h; return; }
      if (e === 1) return;
      break;
    }
    p[o] = U() * w;
    p[o + 1] = h - Math.abs(U()) * (0.1 * w);
  }

  /** `XomOglDrawPsParticleSet` 0x11b3fd0: one quad per slot up to the last active one. */
  draw(): void {
    const f = this.f, n = this.lastActive + 1, p = this.pos, v = this.vel;
    const P = this.aPos.array as Float32Array, T = this.aUv.array as Float32Array, C = this.aCol.array as Float32Array;
    const stretchOn = f.VelocityStretchEnabled;
    const stretchK = f.VelocityStretchMultiplier / (f.InitialSpeed === 0 ? 1 : f.InitialSpeed);
    const P1 = this.frameP, fw = this.frameW;
    for (let i = 0; i < n; i++) {
      const o = 4 * i, size = p[o + 3];
      // Colour array (else white) × the alpha array.
      const r = this.colour ? this.colour[o] : 1, g = this.colour ? this.colour[o + 1] : 1, b = this.colour ? this.colour[o + 2] : 1;
      const a = size > 0 ? this.alpha[i] : 0;
      const [s, c] = this.rot && !stretchOn ? fastSinCos(this.rot[i]) : [0, 1];
      const fr = this.frame ? Math.trunc(this.frame[i]) & 0xff : 0;
      const col = fr % P1, row = P1 - 1 - Math.trunc(fr / P1);
      const stretch = stretchOn ? Math.hypot(v[o], v[o + 1]) * stretchK : 1;
      let dx = 0, dy = 1;
      if (f.RotationLockMode === RLM_VELOCITY && (v[o] !== 0 || v[o + 1] !== 0)) {
        const l = Math.hypot(v[o], v[o + 1]);
        dx = v[o] / l; dy = v[o + 1] / l;
      }
      // The quad's long axis (the lock direction turned by the rotation) and its normal.
      const ax = c * dx + dy * s, ay = c * dy - s * dx;
      const sx = stretch * size * ax, sy = stretch * size * ay, nx = -size * ay, ny = size * ax;
      const off = this.enableOffsets ? this.offsets[3 * i] : 0;
      const cx = p[o] + ay * off, cy = p[o + 1] - ax * off, z = p[o + 2];
      P.set([cx + nx + sx, cy + ny + sy, z, cx + nx - sx, cy + ny - sy, z, cx - nx - sx, cy - ny - sy, z, cx - nx + sx, cy - ny + sy, z], 12 * i);
      const u0 = col * fw, u1 = (col + 1) * fw, v0 = row * fw, v1 = (row + 1) * fw;
      T.set([u0, v1, u0, v0, u1, v0, u1, v1], 8 * i);
      C.set([r, g, b, a, r, g, b, a, r, g, b, a, r, g, b, a], 16 * i);
    }
    this.geo.setDrawRange(0, n * 6);
    for (const [attr, w] of [[this.aPos, 12], [this.aUv, 8], [this.aCol, 16]] as const) {
      attr.clearUpdateRanges();
      attr.addUpdateRange(0, n * w);
      attr.needsUpdate = true;
    }
    this.mesh.visible = n > 0;
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.geo.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}

/** An effect's emitters with the graphical data of `BaseParticleEffect` (`m_Gd`) or of a
 * `TransientParticleEffect` play. */
class EffectRun {
  emitters: Emitter[] = [];
  timers: number[] = [];
  activate: boolean[] = [];
  originalTargetAlpha: number[] = [];
  gd = 0;
  tLast = 0;
  detached = { x: 0, y: 0, z: 0, rz: 0 };
  /** The emitters are children of the parent's locator node (attached in transform mode). */
  parented = false;
  /** The original's own `DestroyEmitter` request (all emitters done), served next step. */
  pendingDestroy = false;
  readonly id: number;
  readonly resource: string;
  readonly layer: ParticleLayer;
  readonly def: EffectDef | null;
  private host: Particles;
  constructor(id: number, resource: string, layer: ParticleLayer, def: EffectDef | null, host: Particles) {
    this.id = id;
    this.resource = resource;
    this.layer = layer;
    this.def = def;
    this.host = host;
  }

  /** `SetupEmitters` 0xd4dbb0: one emitter per slot, inactive. */
  setup(): void {
    this.destroy();
    const d = this.def;
    if (!d) return;
    this.emitters = d.emitters.map((e, i) => {
      const em = new Emitter(e, d.textures[i], this.layer, d.ranks[i]);
      em.setActive(false, false);
      this.host.adopt(em);
      return em;
    });
    this.timers = this.emitters.map(() => 0);
    this.activate = this.emitters.map(() => false);
    this.gd &= ~GD_TARGET_ALPHA;
  }

  /** `StartParticles_Graphical` 0xd4df50: each emitter's timer = the last update time + its delay. */
  start(): void {
    this.timers = this.emitters.map((_, i) => (this.def?.delays[i] ?? 0) + this.tLast);
  }

  /** `StopParticlesGraphical(bStopAlive)` 0xd4d9d0. */
  stop(stopAlive: boolean): void {
    for (const e of this.emitters) e.setActive(false, stopAlive);
  }

  /** `DestroyEmitters(true, true)`: the emitters die at once. */
  destroy(): void {
    for (const e of this.emitters) e.dead = true;
    this.emitters = [];
    this.timers = [];
    this.activate = [];
  }

  /** The start delays of `GraphicUpdate`: due emitters are marked; returns whether one still
   * waits and whether every emitter is done (none waits, none emits or has particles). */
  delays(t: number): { waiting: boolean; done: boolean } {
    let waiting = 0;
    this.emitters.forEach((_, i) => {
      if (this.timers[i] > 0) {
        waiting++;
        if (this.timers[i] <= t) { this.activate[i] = true; this.timers[i] = 0; }
      } else if (this.activate[i]) waiting++;
    });
    return { waiting: waiting > 0, done: waiting === 0 && this.emitters.length > 0 && !this.emitters.some(e => e.busy()) };
  }

  /** The marked emitters start (`XEmitterEntity::SetActive(true, false)`). */
  fire(): void {
    this.emitters.forEach((e, i) => { if (this.activate[i]) { this.activate[i] = false; e.setActive(true, false); } });
  }
}

export class Particles {
  /** One group per particle layer, placed in its pass by the renderer. */
  readonly groups = Object.fromEntries(LAYERS.map(l => [l, new THREE.Group()])) as Record<ParticleLayer, THREE.Group>;
  /** Each layer's draw order among its scene's objects (set by the renderer); emitters keep
   * their creation order inside a layer. */
  readonly order = Object.fromEntries(LAYERS.map(l => [l, 0])) as Record<ParticleLayer, number>;
  private adopted = 0;
  /** The world matrix of locator `name` of sim object `mesh`'s mesh as the renderer last posed it
   * (set by the renderer); null while the mesh is not loaded or lacks the node. */
  locator: ((mesh: number, name: string) => THREE.Matrix4 | null) | null = null;
  private groundZero = Number.NEGATIVE_INFINITY;
  private effects = new Map<number, EffectRun>();
  private transients: EffectRun[] = [];
  /** `ParticleService::m_pEmitterList`, in creation order. */
  private service: Emitter[] = [];
  /** Steps not applied yet (waiting for effect definitions). */
  private queue: ParticleStep[] = [];
  private stepped = 0;
  /** The previous step's effects. A step's `BaseParticleEffect::GraphicUpdate` runs in its graphic
   * pass, before the step's logic (`TaskManager::Update` 0x11d7a40; RenderFidelity.md "Mesh
   * instance anim states", frame order), so it reads `m_Ld` (position, flags, parent, alpha) as
   * the previous step's logic left it, as the meshes' transforms do; a step's view carries `m_Ld`
   * after its logic and the requests its graphic pass served. */
  private prevEffects = new Map<number, EffectView>();
  private dirty = false;
  private wind = { oldX: 0, edges: -1, wasPeak: false };
  private index: ParticleIndex | null = null;
  private hashes = new Map<number, string>();
  /** Each texture's index in ParticleShaders.xom (first use in the shape list). */
  private texRanks = new Map<string, number>();
  private defs = new Map<string, EffectDef | null>();
  private loading = new Set<string>();

  constructor() {
    void assetJson<ParticleIndex>('particles/index.json').then(idx => {
      this.index = idx;
      this.hashes = new Map(Object.keys(idx.effects).map(n => [nameHash(n), n]));
      for (const s of idx.shapes) {
        const png = s.material.Textures?.[0]?.TextureMap?.Texture?.png;
        if (png && !this.texRanks.has(png)) this.texRanks.set(png, this.texRanks.size);
      }
    }).catch(e => console.warn('particles index', e));
  }

  /** A new emitter joins the service and its layer. */
  adopt(e: Emitter): void {
    e.setGroundZero(this.groundZero);
    this.service.push(e);
    e.mesh.renderOrder = this.order[e.layer] + (e.texRank * 2 + (e.f.MatAdd ? 1 : 0)) * RANK_STEP + (this.adopted++ % 100000) * ADOPT_STEP;
    this.groups[e.layer].add(e.mesh);
  }

  /** Queues a step's particle view (applied by `drain`). */
  push(s: ParticleStep): void {
    this.queue.push(s);
    this.drain();
  }

  /** Applies the queued steps in order up to the first one whose effects are still loading. */
  private drain(): void {
    let k = 0;
    while (k < this.queue.length && this.loaded(this.queue[k])) this.step(this.queue[k++]);
    if (k) this.queue.splice(0, k);
  }

  /** Whether every effect the step initialises has its definition (loads the missing ones). */
  private loaded(s: ParticleStep): boolean {
    if (!this.index) return false;
    let ok = true;
    const need = (name: string): void => {
      if (this.defs.has(name)) return;
      ok = false;
      if (this.loading.has(name)) return;
      this.loading.add(name);
      void this.load(name).then(d => this.defs.set(name, d));
    };
    for (const e of s.effects) if (e.served & DIRTY_INIT || !this.effects.has(e.id)) need(effectName(e.resource));
    for (const t of s.transients) {
      const n = t.dirty & T_INIT ? this.hashes.get(t.effect) : undefined;
      if (n) need(n);
    }
    return ok;
  }

  private async load(name: string): Promise<EffectDef | null> {
    const idx = this.index;
    const path = idx?.effects[name];
    if (!idx || !path) return null;
    try {
      const effect = await assetJson<EffectJson>(path);
      const emitters = await Promise.all(effect.emitters.map(e => assetJson<EmitterJson>(e.emitter)));
      const pngs = emitters.map(e => (idx.shapes.find(s => s.name === e.texture?.shape_name) ?? idx.shapes[0])?.material.Textures?.[0]?.TextureMap?.Texture?.png);
      // `XEmitterEntity::Initialize`: shape "PS_<ShaderResource>_<TextureResource>" of
      // ParticleShaders.xom, the first one when none matches.
      const textures = pngs.map(png => (png ? particleTexture(png) : null));
      const ranks = pngs.map(png => (png ? this.texRanks.get(png) ?? 0 : 0));
      return { emitters, delays: effect.emitters.map(e => e.delay_s), textures, ranks };
    } catch (e) {
      console.warn('particles', name, e);
      return null;
    }
  }

  /** One sim step: the effects' and transients' graphic updates, the wind, then
   * `ParticleService::Update(m_Time)` (1/60 s per presented frame). */
  private step(s: ParticleStep): void {
    const t = s.tick / 60;
    const first = this.stepped++ === 0;
    this.dirty = true;
    if (s.waterLevel !== this.groundZero) {
      this.groundZero = s.waterLevel;
      for (const e of this.service) e.setGroundZero(s.waterLevel);
    }
    const seen = new Set<number>();
    for (const v of s.effects) {
      seen.add(v.id);
      // An effect the step's logic created had no graphic update in it (nothing served); one a
      // client finds running when it starts goes from the step's state.
      const g = this.prevEffects.get(v.id);
      if (g) this.graphicUpdate({ ...g, served: v.served }, t, first);
      else if (first) this.graphicUpdate(v, t, first);
    }
    this.prevEffects = new Map(s.effects.map(v => [v.id, v]));
    // An effect that lost its effect data: `GraphicalCleanUp` (stopped and destroyed at once).
    for (const [id, r] of this.effects) if (!seen.has(id)) { r.destroy(); this.effects.delete(id); }
    for (const tr of s.transients) this.transient(tr, t);
    this.transients = this.transients.filter(r => {
      const { done } = r.delays(t);
      r.fire();
      if (done || !r.emitters.length) r.destroy();
      return r.emitters.length > 0;
    });
    this.windUpdate(s);
    for (const e of this.service) e.update(t);
    if (this.service.some(e => e.dead)) {
      for (const e of this.service) if (e.dead) e.dispose();
      this.service = this.service.filter(e => !e.dead);
    }
  }

  /** `BaseParticleEffect::GraphicUpdate` 0xd4e430 with the requests the sim served. A client
   * that starts in the middle of a match (`first`) starts the effects already running. */
  private graphicUpdate(v: EffectView, t: number, first: boolean): void {
    let r = this.effects.get(v.id);
    let d = v.served;
    if (!r || r.resource !== v.resource) {
      r?.destroy();
      r = new EffectRun(v.id, v.resource, layerOf(v.layer), this.defs.get(effectName(v.resource)) ?? null, this);
      this.effects.set(v.id, r);
      if (first && v.flags & LD_STARTED) d |= DIRTY_INIT;
    }
    if (r.pendingDestroy) d |= DIRTY_DESTROY;
    r.pendingDestroy = false;
    if (v.flags & LD_UPDATED && !(r.gd & GD_TIME)) { r.gd |= GD_TIME; r.tLast = t; }
    if (d & DIRTY_DETACH) r.gd &= ~GD_ATTACHED;
    if (d & DIRTY_DETACH_AFTER) r.gd |= GD_DETACH_PENDING;
    if (d & DIRTY_SET_ALLOCATOR) r.gd |= GD_ATTACHED;
    if (d & DIRTY_SET_ALLOCATOR_TRANSFORM) r.gd |= GD_ATTACHED_TRANSFORM;
    if (d & DIRTY_STOP) { r.gd &= ~GD_ATTACHED; r.stop(!(d & DIRTY_KEEP_ALIVE)); }
    if (d & DIRTY_DESTROY) r.destroy();
    if (d & DIRTY_INIT) {
      r.stop(true);
      r.setup();
      r.parented = false;
    }
    if (d & (DIRTY_START | DIRTY_INIT)) r.start();
    if (!(v.flags & LD_STARTED)) return;
    if (r.delays(t).done) r.pendingDestroy = true;
    this.updateEmitterPositions(r, v);
    // UpdateTargetAlpha: with m_Flags bit 8 the emitters' TargetAlpha follows the effect's alpha.
    if (!(r.gd & GD_TARGET_ALPHA)) {
      r.originalTargetAlpha = r.emitters.map(e => e.f.TargetAlpha);
      r.gd |= GD_TARGET_ALPHA;
    }
    if (v.flags & LD_TARGET_ALPHA) r.emitters.forEach((e, i) => { if (r.originalTargetAlpha[i] >= 0) e.f.TargetAlpha = v.alpha; });
    r.fire();
  }

  /** `BaseParticleEffect::UpdateEmitterPositions` 0xd4cc90 with the view's `m_ParentInfo`.
   * Attached without the transform flag: the parent mesh's locator (`QueryNodeTransform`; nothing
   * moves without it) gives the position and the z rotation `axis.z × angle` of
   * `XMatrix3::GetRotation` (negated for a reversed parent, none with the fixed-rotation flag)
   * + π/2 + the additional rotation. Detached: the position frozen at the detach. Then the
   * pending requests in the original's order: a transform attach (`m_Gd` 0xed | 1) makes the
   * emitters children of the locator node (`follow`, mirrored in x when reversed) and places
   * nothing; a detach (0xfa | 0x10) freezes the current position (a transform-mode one leaves the
   * node first). An attached transform-mode effect only follows its node. */
  private updateEmitterPositions(r: EffectRun, v: EffectView): void {
    let [x, y, z] = v.pos, rz = v.euler[2];
    const transform = (v.flags & LD_TRANSFORM) !== 0;
    const node = v.parent === NO_PARENT ? null : this.locator?.(v.parent, v.locator) ?? null;
    if (r.gd & GD_ATTACHED && !transform) {
      if (!node) return;
      const e = node.elements;
      [x, y, z] = [e[12], e[13], e[14]];
      if (v.flags & LD_FIXED_ROTATION) rz = Math.fround(HALF_PI + v.parentRotation);
      else {
        // XMatrix43's row i is the image of axis i: three.js's column i.
        const g = getRotation([e[0], e[1], e[2], e[4], e[5], e[6], e[8], e[9], e[10]]);
        const a = Math.fround(g.z * g.angle);
        rz = Math.fround(Math.fround((v.parentReverse ? -a : a) + HALF_PI) + v.parentRotation);
      }
    } else if (!(r.gd & GD_ATTACHED) && r.gd & GD_DETACHED) ({ x, y, z, rz } = r.detached);
    if (r.gd & GD_ATTACHED_TRANSFORM) {
      r.gd = (r.gd & ~(GD_ATTACHED_TRANSFORM | GD_DETACHED)) | GD_ATTACHED;
      if (transform) {
        if (v.parent === NO_PARENT) return;
        r.parented = true;
        for (const em of r.emitters) {
          if (v.parentReverse) em.sx = -em.sx;
          if (node) em.follow(node);
        }
        return;
      }
    } else if (r.gd & GD_DETACH_PENDING) {
      r.gd = (r.gd & ~(GD_ATTACHED | GD_DETACH_PENDING)) | GD_DETACHED;
      r.detached = { x, y, z, rz };
      if (transform) {
        if (v.parent === NO_PARENT) return;
        r.parented = false;
      }
    } else if (r.gd & GD_ATTACHED && transform) {
      if (r.parented && node) for (const em of r.emitters) em.follow(node);
      return;
    }
    for (const em of r.emitters) em.place(x, y, z, rz);
  }

  /** A transient's requests: stop (destroyed at once), or initialise, position and start a
   * new play (its delays from now). */
  private transient(tr: TransientView, t: number): void {
    if (tr.dirty & T_STOP) {
      // The newest play of that transient slot.
      for (let k = this.transients.length - 1; k >= 0; k--) {
        if (this.transients[k].id === tr.id) { this.transients[k].destroy(); break; }
      }
      return;
    }
    const name = tr.dirty & T_INIT ? this.hashes.get(tr.effect) : undefined;
    if (!name) return;
    const r = new EffectRun(tr.id, name, layerOf(tr.layer), this.defs.get(name) ?? null, this);
    r.tLast = t;
    r.setup();
    if (tr.dirty & T_POSITION) for (const e of r.emitters) e.place(tr.pos[0], tr.pos[1], tr.pos[2], 0);
    if (tr.dirty & T_START) r.start();
    this.transients.push(r);
  }

  /** `BackgroundMan::GraphicUpdate` 0xb926a0 on the theme's wind effect: max wind speed 60,
   * velocity cap 500; `SetParticleWind` 0xb922c0: wind (60 wx, |wx| < 0.25 ? -15 : 0), edges (8
   * the top while |wx| < 0.25, else 4 left for wind to the right, 2 right), and
   * `ForceWindDirection` when the direction or the edges change or the emitters had not peaked. */
  private windUpdate(s: ParticleStep): void {
    const r = this.effects.get(s.windEffect);
    if (!r?.emitters.length) return;
    const wx = s.wind;
    const calm = Math.abs(wx) < 0.25;
    const edges = calm ? 8 : wx > 0 ? 4 : 2;
    for (const e of r.emitters) {
      e.setMaxWind(60, 500);
      e.setWind(wx * 60, calm ? -15 : 0, e.f.Edges);
    }
    const peak = r.emitters.some(e => e.peakReached);
    if (Math.sign(this.wind.oldX) !== Math.sign(wx) || edges !== this.wind.edges || !this.wind.wasPeak) {
      this.wind.edges = edges;
      for (const e of r.emitters) {
        e.setWind(wx * 60, calm ? -15 : 0, edges);
        e.forceWind();
      }
    }
    this.wind.wasPeak = peak;
    this.wind.oldX = wx;
  }

  /** Applies the steps whose effects finished loading, then builds the quads of every emitter
   * (once per rendered frame after new steps). */
  draw(): void {
    if (this.queue.length) this.drain();
    if (!this.dirty) return;
    this.dirty = false;
    for (const e of this.service) e.draw();
  }
}

/** The effect name of a `BaseParticleEffect` resource (the file name without its extension). */
function effectName(resource: string): string {
  return resource.replace(/^.*[/\\]/, '').replace(/\.[^.]*$/, '');
}

const textureCache = new Map<string, THREE.Texture>();
/** A particle texture as ParticleShaders.xom samples it: linear, no mip levels, repeat. */
function particleTexture(png: string): THREE.Texture {
  let t = textureCache.get(png);
  if (!t) {
    t = new THREE.TextureLoader().load(assetUrl(png));
    t.flipY = false;
    t.colorSpace = THREE.NoColorSpace;
    t.generateMipmaps = false;
    t.minFilter = THREE.LinearFilter;
    t.magFilter = THREE.LinearFilter;
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    textureCache.set(png, t);
  }
  return t;
}
